import type { StateStorage } from "zustand/middleware"

/**
 * Wraps localStorage so persist() writes don't block the main thread on every state change.
 * Zustand's persist middleware calls setItem() on every set() by default — for stores driven by
 * high-frequency events (dragging a control point, moving an overlay dot), that means a
 * synchronous JSON.stringify + localStorage.setItem on every single pointermove, which is a
 * major source of drag jank. This coalesces rapid-fire writes into one, `delayMs` after the
 * last one, while keeping reads/removes instant (they're rare and not perf-sensitive).
 *
 * Pending writes are flushed immediately on tab hide/close so a mid-drag navigation away doesn't
 * lose the last few seconds of edits.
 */
export function createDebouncedStorage(delayMs = 400): StateStorage {
  const timers = new Map<string, ReturnType<typeof setTimeout>>()
  const pending = new Map<string, string>()

  function flush(name: string) {
    const timer = timers.get(name)
    if (timer) clearTimeout(timer)
    timers.delete(name)
    const value = pending.get(name)
    if (value !== undefined) {
      pending.delete(name)
      window.localStorage.setItem(name, value)
    }
  }

  function flushAll() {
    for (const name of Array.from(pending.keys())) flush(name)
  }

  if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", flushAll)
    window.addEventListener("pagehide", flushAll)
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushAll()
    })
  }

  return {
    getItem: (name) => (typeof window === "undefined" ? null : window.localStorage.getItem(name)),
    setItem: (name, value) => {
      if (typeof window === "undefined") return
      pending.set(name, value)
      const existing = timers.get(name)
      if (existing) clearTimeout(existing)
      timers.set(name, setTimeout(() => flush(name), delayMs))
    },
    removeItem: (name) => {
      if (typeof window === "undefined") return
      const existing = timers.get(name)
      if (existing) clearTimeout(existing)
      timers.delete(name)
      pending.delete(name)
      window.localStorage.removeItem(name)
    },
  }
}
